/**
 * The authoring rules, kept out of `template-compile.ts` so the editor can run
 * them as the author types. That file reaches Twilio's payload schema, whose
 * module graph the app must not pull into its own stricter typecheck; this one
 * touches only the content model and the shared Liquid regex.
 */

import {
  INVALID_URL_MESSAGE,
  linkUrlError,
} from "@hightouch/lib/cep/link-url-error";
import {
  isE164,
  urlWithOptionalTrailingVariable,
} from "@hightouch/lib/cep/messaging/providers/sms/twilio-content-formats";
import {
  LIQUID_VAR_REGEX,
  variableNameFromMatch,
} from "@hightouch/lib/cep/template-vars";
import {
  WHATSAPP_BODY_MAX,
  WHATSAPP_BUTTON_LABEL_MAX,
  WHATSAPP_COPY_CODE_MAX,
  WHATSAPP_FOOTER_MAX,
  WHATSAPP_HEADER_MAX_VARIABLES,
  WHATSAPP_HEADER_TEXT_MAX,
  WHATSAPP_MAX_BUTTONS,
  WHATSAPP_MAX_CALL_BUTTONS,
  WHATSAPP_MAX_COPY_CODE_BUTTONS,
  WHATSAPP_MAX_EMOJIS,
  WHATSAPP_MAX_URL_BUTTONS,
  WHATSAPP_MAX_VARIABLES,
  WHATSAPP_URL_MAX,
  type WhatsAppTemplateContent,
} from "@hightouch/lib/cep/types/whatsapp-template-content";
import type { WhatsAppTemplateVariables } from "@hightouch/lib/cep/types/whatsapp-template";

/** Where an authoring error belongs, so the editor can put it on the field. */
export type WhatsAppContentField =
  | { kind: "template" }
  | { kind: "header" }
  | { kind: "body" }
  | { kind: "footer" }
  | { kind: "buttons" }
  | { kind: "button"; index: number; part: "label" | "value" }
  | { kind: "sample"; name: string };

export type WhatsAppFieldError = {
  field: WhatsAppContentField;
  message: string;
};

/**
 * Assigns `{{n}}` positions across every component in one sequence.
 *
 * Twilio's Content API uses a single flat namespace for the whole template and
 * maps it onto Meta's per-component numbering itself, so a variable's position
 * does not restart in the header or a button.
 *
 * The same tag used twice shares one position: the send supplies each position
 * once, and two occurrences of identical Liquid always render alike.
 */
class PositionAssigner {
  private readonly positionByTag = new Map<string, string>();
  /** In position order. */
  private readonly entries: Array<{
    liquid: string;
    /** The variable rendered here: the tag's own, or the one in a tracked link's URL; null for a literal tracked link. */
    name: string | null;
    tracked: boolean;
  }> = [];

  /** Replaces each Liquid tag with its position, recording both. */
  substitute(text: string): string {
    let result = "";
    let cursor = 0;

    for (const match of text.matchAll(LIQUID_VAR_REGEX)) {
      const tag = match[0];
      const start = match.index ?? 0;
      let position = this.positionByTag.get(tag);
      if (position === undefined) {
        position = this.push({
          liquid: tag,
          name: variableNameFromMatch(match) ?? tag,
          tracked: false,
        });
        this.positionByTag.set(tag, position);
      }
      result += text.slice(cursor, start) + `{{${position}}}`;
      cursor = start + tag.length;
    }

    return result + text.slice(cursor);
  }

  /**
   * A tracked link's whole URL fills one position, never shared, since each
   * button needs its own short id. The send supplies its value, so it takes no sample.
   */
  reserveTrackedLink(url: string, name: string | null): string {
    return this.push({ liquid: url, name, tracked: true });
  }

  private push(entry: {
    liquid: string;
    name: string | null;
    tracked: boolean;
  }): string {
    this.entries.push(entry);
    return String(this.entries.length);
  }

  variables(): WhatsAppTemplateVariables {
    return Object.fromEntries(
      this.entries.map(({ liquid, tracked }, index) => [
        String(index + 1),
        { kind: tracked ? "tracked_link" : "text", liquid },
      ]),
    );
  }

  /** The author's variable name for each position that takes a sample. */
  nameByPosition(): Record<string, string> {
    return Object.fromEntries(
      this.entries.flatMap(({ name, tracked }, index) =>
        tracked || name === null ? [] : [[String(index + 1), name]],
      ),
    );
  }

  /** The variables Meta reviews, each taking a sample. */
  variableNames(): string[] {
    return this.entries.flatMap(({ name, tracked }) =>
      tracked || name === null ? [] : [name],
    );
  }

  /** Every variable a send renders, a tracked link's included. */
  renderedVariableNames(): string[] {
    return this.entries.flatMap(({ name }) => (name === null ? [] : [name]));
  }

  trackedPositions(): string[] {
    return this.entries.flatMap(({ tracked }, index) =>
      tracked ? [String(index + 1)] : [],
    );
  }
}

/** Meta reviews a tracked link with this in place of a real short id. */
export const WHATSAPP_TRACKED_LINK_SAMPLE = "a1B2c3";

function countButtons(
  buttons: WhatsAppTemplateContent["buttons"],
  type: WhatsAppTemplateContent["buttons"][number]["type"],
): number {
  return buttons.filter((button) => button.type === type).length;
}

function countVariables(text: string): number {
  return [...text.matchAll(LIQUID_VAR_REGEX)].length;
}

function hasVariable(text: string): boolean {
  return countVariables(text) > 0;
}

/**
 * Meta rejects a body that starts/ends on a variable, because it cannot
 * tell the filled value from the surrounding copy when reviewing. Punctuation
 * next to the variable does not rescue it: "gate {{2}}." is still rejected.
 */
function startsOrEndsWithVariable(compiled: string): boolean {
  return (
    /^[\p{P}\s]*\{\{\d+\}\}/u.test(compiled) ||
    /\{\{\d+\}\}[\p{P}\s]*$/u.test(compiled)
  );
}

/** Meta treats variables separated only by whitespace as adjacent. */
function hasAdjacentVariables(compiled: string): boolean {
  return /\{\{\d+\}\}\s*\{\{\d+\}\}/.test(compiled);
}

/** Meta rejects newlines, tabs and more than four spaces in a row. */
function hasDisallowedWhitespace(text: string): boolean {
  return /[\n\t]| {5}/.test(text);
}

/** The URL schema's issues read as clauses; under a field they start a sentence. */
function sentenceCase(message: string | undefined): string {
  if (!message) return INVALID_URL_MESSAGE;
  return message.charAt(0).toUpperCase() + message.slice(1);
}

/**
 * WhatsApp's formatting markers, which Meta refuses in a text header.
 * @see https://developers.facebook.com/documentation/business-messaging/whatsapp/templates/components
 */
const WHATSAPP_FORMATTING_CHARACTERS = /[*_~`]/;

function countEmojis(text: string): number {
  return [...text.matchAll(/\p{Extended_Pictographic}/gu)].length;
}

/** Words that are not variables; a token counts once it has a letter or digit. */
function countNonVariableWords(compiled: string): number {
  return compiled
    .replace(/\{\{\d+\}\}/g, " ")
    .split(/\s+/)
    .filter((token) => /[\p{L}\p{N}]/u.test(token)).length;
}

type AuthoredButton = WhatsAppTemplateContent["buttons"][number];

/**
 * One button with its Liquid already replaced by positions. A tracked link's
 * URL is our redirect, so only its position is assigned here; the compiler
 * adds the host, which the editor running these rules doesn't know.
 */
export type CompiledButton =
  | (AuthoredButton & { compiledValue: string })
  | (Extract<AuthoredButton, { type?: "link" }> & { trackedPosition: string });

/**
 * A header or footer the author emptied is no header or footer at all, rather
 * than one Meta would refuse as blank.
 */
function presentHeader(
  header: WhatsAppTemplateContent["header"],
): WhatsAppTemplateContent["header"] {
  if (header?.kind === "text") return header.text.trim() === "" ? null : header;
  if (header?.kind === "image") return header.url.trim() === "" ? null : header;
  return null;
}

export type WhatsAppContentValidation = {
  fieldErrors: WhatsAppFieldError[];
  headerText: string | null;
  headerImageUrl: string | null;
  /** Null when absent or blank. */
  footer: string | null;
  body: string;
  buttons: CompiledButton[];
  /** Every position, with the Liquid that fills it and what the send does with it. */
  variables: WhatsAppTemplateVariables;
  /** Position to the example value Meta reviews it with. */
  samples: Record<string, string>;
  /** Each variable's name in position order; samples are keyed by these. */
  variableNames: string[];
  /** Every variable a send renders, in position order, a tracked link's included. */
  renderedVariableNames: string[];
};

/** Each variable the content uses, once: two tags can share a name and its example. */
export function whatsAppVariableNames(
  content: WhatsAppTemplateContent,
): string[] {
  return [...new Set(validateWhatsAppContent(content).variableNames)];
}

/** Each variable a send renders, once, including those inside a tracked link. */
export function whatsAppRenderedVariableNames(
  content: WhatsAppTemplateContent,
): string[] {
  return [...new Set(validateWhatsAppContent(content).renderedVariableNames)];
}

/**
 * Checks the authored content and returns it with `{{n}}` positions assigned.
 * Lengths are measured on the substituted text, which is what Meta sees.
 */
export function validateWhatsAppContent(
  content: WhatsAppTemplateContent,
): WhatsAppContentValidation {
  const fieldErrors: WhatsAppFieldError[] = [];
  const fail = (field: WhatsAppContentField, message: string): void => {
    fieldErrors.push({ field, message });
  };
  const assigner = new PositionAssigner();
  const header = presentHeader(content.header);
  const footer = content.footer?.trim() ? content.footer : null;

  // Header first, so positions read in the order the recipient sees them.
  let compiledHeaderText: string | null = null;
  if (header?.kind === "text") {
    if (countVariables(header.text) > WHATSAPP_HEADER_MAX_VARIABLES) {
      fail(
        { kind: "header" },
        `A header allows at most ${WHATSAPP_HEADER_MAX_VARIABLES} variable`,
      );
    }
    compiledHeaderText = assigner.substitute(header.text);
    if (compiledHeaderText.length > WHATSAPP_HEADER_TEXT_MAX) {
      fail(
        { kind: "header" },
        `Header is ${compiledHeaderText.length} characters; the limit is ${WHATSAPP_HEADER_TEXT_MAX}`,
      );
    }
    if (WHATSAPP_FORMATTING_CHARACTERS.test(compiledHeaderText)) {
      fail(
        { kind: "header" },
        "Header cannot contain formatting characters: * _ ~ `",
      );
    }
  } else if (header?.kind === "image") {
    const url = header.url.trim();
    if (hasVariable(url)) {
      fail({ kind: "header" }, "An image header cannot contain variables");
    } else if (!url.startsWith("https://")) {
      fail({ kind: "header" }, "Image URL must use HTTPS");
    } else if (!URL.canParse(url)) {
      fail({ kind: "header" }, "Enter a valid image URL");
    }
  }

  if (content.body.trim() === "") {
    fail({ kind: "body" }, "Template body is empty");
  }
  const compiledBody = assigner.substitute(content.body);
  if (compiledBody.length > WHATSAPP_BODY_MAX) {
    fail(
      { kind: "body" },
      `Body is ${compiledBody.length} characters; the limit is ${WHATSAPP_BODY_MAX}`,
    );
  }
  if (startsOrEndsWithVariable(compiledBody)) {
    fail(
      { kind: "body" },
      "Body cannot start or end with a variable, and punctuation next to it does not count",
    );
  }
  if (hasAdjacentVariables(compiledBody)) {
    fail(
      { kind: "body" },
      "Variables in the body need at least one word between them",
    );
  }
  if (hasDisallowedWhitespace(content.body)) {
    fail(
      { kind: "body" },
      "Body cannot contain newlines, tabs or more than four spaces in a row",
    );
  }
  // Meta's ratio: for x variables, at least 2x + 1 words that are not variables.
  // @see https://www.twilio.com/docs/content/using-variables-with-content-api
  const bodyVariables = countVariables(content.body);
  if (
    bodyVariables > 0 &&
    countNonVariableWords(compiledBody) <= 2 * bodyVariables
  ) {
    fail(
      { kind: "body" },
      "The body needs more than twice as many words as variables",
    );
  }

  if (footer !== null) {
    if (hasVariable(footer)) {
      fail({ kind: "footer" }, "Footer cannot contain variables");
    }
    if (footer.length > WHATSAPP_FOOTER_MAX) {
      fail(
        { kind: "footer" },
        `Footer is ${footer.length} characters; the limit is ${WHATSAPP_FOOTER_MAX}`,
      );
    }
  }

  if (content.buttons.length > WHATSAPP_MAX_BUTTONS) {
    fail(
      { kind: "buttons" },
      `A template allows at most ${WHATSAPP_MAX_BUTTONS} buttons`,
    );
  }
  if (countButtons(content.buttons, "link") > WHATSAPP_MAX_URL_BUTTONS) {
    fail(
      { kind: "buttons" },
      `A template allows at most ${WHATSAPP_MAX_URL_BUTTONS} link buttons`,
    );
  }
  if (countButtons(content.buttons, "phone") > WHATSAPP_MAX_CALL_BUTTONS) {
    fail(
      { kind: "buttons" },
      `A template allows at most ${WHATSAPP_MAX_CALL_BUTTONS} phone button`,
    );
  }
  if (
    countButtons(content.buttons, "copy_code") > WHATSAPP_MAX_COPY_CODE_BUTTONS
  ) {
    fail(
      { kind: "buttons" },
      `A template allows at most ${WHATSAPP_MAX_COPY_CODE_BUTTONS} copy code button`,
    );
  }

  const compiledButtons: CompiledButton[] = content.buttons.map(
    (button, index) => {
      const label = (part: "label" | "value"): WhatsAppContentField => ({
        kind: "button",
        index,
        part,
      });

      if (button.type === "copy_code") {
        const compiledCode = assigner.substitute(button.code);
        if (button.code.trim() === "") {
          fail(label("value"), "Every copy code button needs a code");
        } else if (compiledCode.length > WHATSAPP_COPY_CODE_MAX) {
          fail(
            label("value"),
            `Code is ${compiledCode.length} characters; the limit is ${WHATSAPP_COPY_CODE_MAX}`,
          );
        }
        return { ...button, compiledValue: compiledCode };
      }

      if (button.label.trim() === "") {
        fail(label("label"), "Every button needs a label");
      }
      if (hasVariable(button.label)) {
        fail(label("label"), "A button label cannot contain variables");
      }
      if (button.label.length > WHATSAPP_BUTTON_LABEL_MAX) {
        fail(
          label("label"),
          `Label is ${button.label.length} characters; the limit is ${WHATSAPP_BUTTON_LABEL_MAX}`,
        );
      }

      if (button.type === "phone") {
        if (hasVariable(button.phone)) {
          fail(label("value"), "A phone number cannot contain variables");
        } else if (!isE164(button.phone)) {
          fail(
            label("value"),
            "Enter a phone number in E.164 format, like +15555550123",
          );
        }
        return { ...button, compiledValue: button.phone };
      }

      // A tracked URL is checked like any other, so turning tracking on or off
      // can't make a template invalid. Only its redirect reaches Meta, so its
      // variable gets a placeholder for the check rather than a position.
      const compiledUrl = button.tracked
        ? button.url.replace(LIQUID_VAR_REGEX, "{{1}}")
        : assigner.substitute(button.url);
      const url = urlWithOptionalTrailingVariable.safeParse(compiledUrl);
      // A literal link can say what's wrong with it, an unparseable scheme included; a templated one fails the variable rule.
      const urlError = compiledUrl.includes("{{")
        ? url.success
          ? undefined
          : sentenceCase(url.error.issues[0]?.message)
        : (linkUrlError(compiledUrl) ??
          (url.success ? undefined : INVALID_URL_MESSAGE));
      if (urlError !== undefined) {
        fail(label("value"), urlError);
      } else if (compiledUrl.length > WHATSAPP_URL_MAX) {
        fail(
          label("value"),
          `URL is ${compiledUrl.length} characters; the limit is ${WHATSAPP_URL_MAX}`,
        );
      }
      if (button.tracked) {
        // The URL rule allows one variable, so a tracked link renders at most one.
        const [match] = button.url.matchAll(LIQUID_VAR_REGEX);
        return {
          ...button,
          trackedPosition: assigner.reserveTrackedLink(
            button.url,
            match ? (variableNameFromMatch(match) ?? match[0]) : null,
          ),
        };
      }
      return { ...button, compiledValue: compiledUrl };
    },
  );

  // `twilio/text` carries only a body and `whatsapp/card` requires a button, so
  // no type Twilio offers holds a header or footer on its own. Refuse rather
  // than compile a payload that drops what the author wrote.
  if ((header !== null || footer !== null) && content.buttons.length === 0) {
    const parts =
      header !== null && footer !== null
        ? "a header and footer"
        : header !== null
          ? "a header"
          : "a footer";
    fail(
      { kind: "buttons" },
      `A template with ${parts} needs at least one button`,
    );
  }

  const emojiCount = countEmojis(
    [
      header?.kind === "text" ? header.text : "",
      content.body,
      footer ?? "",
      ...content.buttons.map((button) =>
        button.type === "copy_code" ? "" : button.label,
      ),
    ].join(" "),
  );
  if (emojiCount > WHATSAPP_MAX_EMOJIS) {
    fail(
      { kind: "template" },
      `A template allows at most ${WHATSAPP_MAX_EMOJIS} emojis; this one has ${emojiCount}`,
    );
  }

  const nameByPosition = assigner.nameByPosition();
  if (Object.keys(nameByPosition).length > WHATSAPP_MAX_VARIABLES) {
    fail(
      { kind: "template" },
      `A template allows at most ${WHATSAPP_MAX_VARIABLES} variables`,
    );
  }
  const compiledSamples: Record<string, string> = {};
  for (const [position, name] of Object.entries(nameByPosition)) {
    // The name is the author's, so an inherited member of Object.prototype
    // would otherwise answer the lookup with a function.
    const sample = Object.hasOwn(content.samples, name)
      ? content.samples[name]
      : undefined;
    if (sample === undefined || sample.trim() === "") {
      fail(
        { kind: "sample", name },
        `Variable "${name}" needs an example value`,
      );
      continue;
    }
    if (hasDisallowedWhitespace(sample)) {
      fail(
        { kind: "sample", name },
        `The example for "${name}" cannot contain newlines, tabs or more than four spaces in a row`,
      );
    }
    compiledSamples[position] = sample;
  }
  for (const position of assigner.trackedPositions()) {
    compiledSamples[position] = WHATSAPP_TRACKED_LINK_SAMPLE;
  }

  return {
    fieldErrors,
    headerText: compiledHeaderText,
    headerImageUrl: header?.kind === "image" ? header.url.trim() : null,
    footer,
    body: compiledBody,
    buttons: compiledButtons,
    variables: assigner.variables(),
    samples: compiledSamples,
    variableNames: assigner.variableNames(),
    renderedVariableNames: assigner.renderedVariableNames(),
  };
}
